import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, User, Sparkles, Maximize2, Minimize2, Minus, Trash2, Mic, Code } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import montyAvatar from '@/assets/monty-snake-bot.png';
import { VoiceChatMode } from './VoiceChatMode';
import { TextCodingMode, CodingProblem, UserContext } from './chatbot/TextCodingMode';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  topic?: string; // Track which topic this message relates to
}

interface SiteChatbotProps {
  className?: string;
}

// Follow-up suggestions based on topic categories
const followUpSuggestions: Record<string, string[]> = {
  salary: [
    "How do Python salaries compare to JavaScript?",
    "What skills increase Python developer pay?",
    "Salary expectations for senior Python roles",
    "Remote vs office Python salary differences"
  ],
  hiring: [
    "Best interview questions for Python devs",
    "How long should the interview process be?",
    "What tech stack should I mention in job ads?",
    "Red flags when hiring Python developers"
  ],
  interview: [
    "Most common Python coding challenges",
    "How to prepare for system design interviews",
    "What do top Python candidates look for?",
    "Async/await questions in interviews"
  ],
  career: [
    "Career path from junior to senior Python dev",
    "Should I learn FastAPI or Django?",
    "Is Python still worth learning in 2026?",
    "Best certifications for Python developers"
  ],
  default: [
    "Python salary benchmarks 2026",
    "Tips for hiring Python developers",
    "Prepare for Python interviews",
    "Python career advice"
  ]
};

// Detect topic from user message
function detectTopic(message: string): string {
  const lowerMsg = message.toLowerCase();
  if (lowerMsg.includes('salary') || lowerMsg.includes('pay') || lowerMsg.includes('£') || lowerMsg.includes('$') || lowerMsg.includes('compensation')) {
    return 'salary';
  }
  if (lowerMsg.includes('hiring') || lowerMsg.includes('hire') || lowerMsg.includes('recruit') || lowerMsg.includes('job ad') || lowerMsg.includes('job description')) {
    return 'hiring';
  }
  if (lowerMsg.includes('interview') || lowerMsg.includes('prep') || lowerMsg.includes('coding challenge') || lowerMsg.includes('technical question')) {
    return 'interview';
  }
  if (lowerMsg.includes('career') || lowerMsg.includes('learn') || lowerMsg.includes('path') || lowerMsg.includes('growth') || lowerMsg.includes('senior') || lowerMsg.includes('junior')) {
    return 'career';
  }
  return 'default';
}

// Detect if user wants a mock interview (voice)
function detectMockInterviewRequest(message: string): boolean {
  const lowerMsg = message.toLowerCase();
  const mockKeywords = [
    'mock interview',
    'practice interview',
    'interview practice',
    'simulate interview',
    'do a mock',
    'behavioral interview',
    'technical interview practice',
    'system design interview',
    'voice interview'
  ];
  return mockKeywords.some(keyword => lowerMsg.includes(keyword));
}

// Detect if user wants coding practice (any form - voice or text)
function detectCodingRequest(message: string): boolean {
  const lowerMsg = message.toLowerCase();
  const codingKeywords = [
    'coding practice',
    'practice coding',
    'solve a problem',
    'leetcode',
    'algorithm practice',
    'data structure practice',
    'coding challenge',
    'write some code',
    'code a solution',
    'try a coding problem',
    'practice python coding',
    'coding exercise',
    'coding interview',
    'live coding'
  ];
  return codingKeywords.some(keyword => lowerMsg.includes(keyword));
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-chatbot`;

async function streamChat({
  messages,
  sessionId,
  onDelta,
  onDone,
  onError,
}: {
  messages: Message[];
  sessionId: string;
  onDelta: (deltaText: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}) {
  try {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ messages, sessionId }),
    });

    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(errorData.error || `HTTP ${resp.status}`);
    }

    if (!resp.body) throw new Error("No response body");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let streamDone = false;

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") {
          streamDone = true;
          break;
        }

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) onDelta(content);
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    // Final flush
    if (textBuffer.trim()) {
      for (let raw of textBuffer.split("\n")) {
        if (!raw) continue;
        if (raw.endsWith("\r")) raw = raw.slice(0, -1);
        if (raw.startsWith(":") || raw.trim() === "") continue;
        if (!raw.startsWith("data: ")) continue;
        const jsonStr = raw.slice(6).trim();
        if (jsonStr === "[DONE]") continue;
        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) onDelta(content);
        } catch { /* ignore */ }
      }
    }

    onDone();
  } catch (error) {
    onError(error instanceof Error ? error.message : 'Unknown error');
  }
}

export const SiteChatbot: React.FC<SiteChatbotProps> = ({ className }) => {
  // Persist open/minimized state across page navigations
  const [isOpen, setIsOpen] = useState(() => {
    const stored = sessionStorage.getItem('chatbot_is_open');
    return stored === 'true';
  });
  const [isMinimized, setIsMinimized] = useState(() => {
    const stored = sessionStorage.getItem('chatbot_is_minimized');
    return stored === 'true';
  });
  
  // Persist messages across page navigations
  const [messages, setMessages] = useState<Message[]>(() => {
    const stored = sessionStorage.getItem('chatbot_messages');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastTopic, setLastTopic] = useState<string>(() => {
    return sessionStorage.getItem('chatbot_last_topic') || 'default';
  });
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    return localStorage.getItem('chatbot_welcome_seen') === 'true';
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isCodingMode, setIsCodingMode] = useState(false);
  const [showMockInterviewPrompt, setShowMockInterviewPrompt] = useState(false);
  const [showCodingPrompt, setShowCodingPrompt] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Generate or retrieve session ID for conversation tracking
  const [sessionId] = useState<string>(() => {
    const stored = sessionStorage.getItem('chatbot_session_id');
    if (stored) return stored;
    const newId = crypto.randomUUID();
    sessionStorage.setItem('chatbot_session_id', newId);
    return newId;
  });

  // Persist state changes to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('chatbot_is_open', String(isOpen));
  }, [isOpen]);
  
  useEffect(() => {
    sessionStorage.setItem('chatbot_is_minimized', String(isMinimized));
  }, [isMinimized]);
  
  useEffect(() => {
    sessionStorage.setItem('chatbot_messages', JSON.stringify(messages));
  }, [messages]);
  
  useEffect(() => {
    sessionStorage.setItem('chatbot_last_topic', lastTopic);
  }, [lastTopic]);

  // Get current follow-up suggestions based on last topic
  const currentSuggestions = followUpSuggestions[lastTopic] || followUpSuggestions.default;

  // Show welcome popup after 3 seconds if not dismissed before
  useEffect(() => {
    // Clear any stale session data on mount to ensure popup shows
    const hasSeenWelcome = localStorage.getItem('chatbot_welcome_seen');
    if (hasSeenWelcome || isOpen) return;
    
    const timer = setTimeout(() => {
      setShowWelcome(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Auto-hide welcome popup after 15 seconds
  useEffect(() => {
    if (!showWelcome) return;
    
    const timer = setTimeout(() => {
      setShowWelcome(false);
    }, 15000);
    
    return () => clearTimeout(timer);
  }, [showWelcome]);

  const dismissWelcome = () => {
    setShowWelcome(false);
    setWelcomeDismissed(true);
    localStorage.setItem('chatbot_welcome_seen', 'true');
  };

  const openChat = () => {
    dismissWelcome();
    setIsOpen(true);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSend = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    // Voice options temporarily disabled - redirect to text-only coding practice
    if (detectCodingRequest(textToSend)) {
      setShowCodingPrompt(true);
      const userMessage: Message = { role: 'user', content: textToSend, topic: 'interview' };
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Great choice! 💻 Let's practice coding together!\n\n**📝 Text-Based Practice**\nSolve problems at your own pace in the code editor. When you're ready, submit your code and I'll provide a detailed written review.\n\nClick the button below to start!",
        topic: 'interview'
      }]);
      return;
    }

    // Voice mock interview - route to interview prep page
    if (detectMockInterviewRequest(textToSend)) {
      const userMessage: Message = { role: 'user', content: textToSend, topic: 'interview' };
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "🎤 **Voice Mock Interviews are Live!**\n\nPractice with Monty, your AI interviewer:\n- **Technical Q&A** - Python concepts, async, decorators, GIL\n- **System Design** - Scalable APIs and data pipelines\n- **Behavioral (STAR)** - Leadership and conflict scenarios\n- **Live Coding** - Solve problems while talking through your approach\n\n👉 Head to the [Interview Prep page](/interview-prep) to start!\n\n*Tip: Use headphones and find a quiet spot for the best experience.*",
        topic: 'interview'
      }]);
      return;
    }

    // Detect topic for follow-up suggestions
    const topic = detectTopic(textToSend);
    setLastTopic(topic);

    const userMessage: Message = { role: 'user', content: textToSend, topic };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    let assistantContent = '';

    const updateAssistant = (chunk: string) => {
      assistantContent += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantContent, topic } : m));
        }
        return [...prev, { role: 'assistant', content: assistantContent, topic }];
      });
    };

    await streamChat({
      messages: [...messages, userMessage],
      sessionId,
      onDelta: updateAssistant,
      onDone: () => setIsLoading(false),
      onError: (error) => {
        toast.error(error);
        setIsLoading(false);
      },
    });
  };

  // Handle code submission from coding practice mode
  const handleCodeSubmit = async (code: string, problem: CodingProblem, userContext?: UserContext) => {
    setIsCodingMode(false);
    setShowCodingPrompt(false);
    
    // Build context string
    const contextInfo = userContext 
      ? `\n\n**Candidate Profile:**\n- Experience: ${userContext.experienceLevel}\n- Target: ${userContext.targetCompany.replace('-', ' ')}\n- Background: ${userContext.pythonBackground.replace('-', ' ')}`
      : '';
    
    // Add user message showing they submitted code
    const userMessage: Message = { 
      role: 'user', 
      content: `Here's my solution for "${problem.title}":${contextInfo}\n\n\`\`\`python\n${code}\n\`\`\``, 
      topic: 'interview' 
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    let assistantContent = '';

    const updateAssistant = (chunk: string) => {
      assistantContent += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantContent, topic: 'interview' } : m));
        }
        return [...prev, { role: 'assistant', content: assistantContent, topic: 'interview' }];
      });
    };

    // Build review prompt with user context for calibrated feedback
    const levelContext = userContext 
      ? `The candidate is a ${userContext.experienceLevel}-level developer targeting ${userContext.targetCompany.replace('-', ' ')} roles with a ${userContext.pythonBackground.replace('-', ' ')} background. Calibrate your feedback accordingly - be more rigorous for senior/FAANG/hedge-fund candidates.`
      : 'Provide general feedback suitable for a mid-level developer.';

    // Send to AI with context about reviewing their code
    const reviewPrompt: Message[] = [
      ...messages,
      userMessage,
      { 
        role: 'user' as const, 
        content: `Please review my Python code solution for the "${problem.title}" problem. The problem was: ${problem.description}\n\n${levelContext}\n\nProvide feedback on:\n1. Correctness - does it solve the problem?\n2. Time & Space complexity (important for ${userContext?.targetCompany === 'hedge-fund' ? 'quant roles' : userContext?.targetCompany === 'faang' ? 'big tech' : 'this level'})\n3. Code style & Pythonic improvements\n4. Edge cases I might have missed\n5. An optimal solution if mine isn't optimal\n6. Interview tip: what would an interviewer at a ${userContext?.targetCompany.replace('-', ' ') || 'tech company'} expect?` 
      }
    ];

    await streamChat({
      messages: reviewPrompt,
      sessionId,
      onDelta: updateAssistant,
      onDone: () => setIsLoading(false),
      onError: (error) => {
        toast.error(error);
        setIsLoading(false);
      },
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Render markdown content with proper link handling
  const renderMessageContent = (content: string) => {
    return (
      <ReactMarkdown
        components={{
          a: ({ href, children }) => {
            // Handle internal section links
            if (href?.startsWith('#')) {
              return (
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(false);
                    window.location.hash = href;
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  {children}
                </a>
              );
            }
            // External links open in new tab
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                {children}
              </a>
            );
          },
          p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-3 space-y-1.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-3 space-y-1.5">{children}</ol>,
          li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children }) => (
            <code className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-mono">{children}</code>
          ),
          pre: ({ children }) => (
            <pre className="bg-secondary/50 rounded-lg p-3 overflow-x-auto mb-3 text-xs">{children}</pre>
          ),
          h1: ({ children }) => <h1 className="font-bold text-lg mt-3 mb-2 text-foreground">{children}</h1>,
          h2: ({ children }) => <h2 className="font-bold text-base mt-3 mb-2 text-foreground">{children}</h2>,
          h3: ({ children }) => <h3 className="font-semibold text-sm mt-3 mb-1.5 text-foreground">{children}</h3>,
          h4: ({ children }) => <h4 className="font-medium text-sm mt-2 mb-1 text-foreground">{children}</h4>,
          hr: () => <hr className="my-3 border-border" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-primary/50 pl-3 my-3 italic text-muted-foreground">{children}</blockquote>
          ),
          // Table support
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="w-full text-xs border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-secondary/50">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-border/50">{children}</tr>,
          th: ({ children }) => <th className="text-left p-2 font-semibold text-foreground">{children}</th>,
          td: ({ children }) => <td className="p-2 text-muted-foreground">{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };

  if (!isOpen) {
    // Show minimized state with conversation indicator
    if (isMinimized && messages.length > 0) {
      return (
        <div className={cn('fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-40', className)}>
          <div className="flex items-center gap-2 bg-card border border-primary/30 rounded-full pl-4 pr-2 py-2 shadow-lg animate-in slide-in-from-right-2">
            <span className="text-sm text-foreground font-medium">Chat with Monty</span>
            <span className="text-xs text-muted-foreground">({messages.length} messages)</span>
            <button
              onClick={() => { setIsMinimized(false); setIsOpen(true); }}
              className="w-10 h-10 rounded-full overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all"
            >
              <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={cn('fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-40', className)}>
        {/* Welcome popup - larger format */}
        {showWelcome && !welcomeDismissed && (
          <div className="absolute bottom-20 right-0 w-80 sm:w-96 animate-in slide-in-from-bottom-2 fade-in duration-300">
            <div className="bg-card border border-primary/30 rounded-xl shadow-2xl p-5 relative">
              {/* Close button */}
              <button 
                onClick={dismissWelcome}
                className="absolute top-3 right-3 p-1.5 hover:bg-secondary rounded-full transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {/* Pointer arrow */}
              <div className="absolute -bottom-2 right-8 w-4 h-4 bg-card border-r border-b border-primary/30 transform rotate-45" />
              
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 shadow-lg ring-2 ring-primary/30">
                  <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-foreground text-base mb-1">👋 Hi, I'm Monty!</p>
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                    Your Python career assistant. I can help with salary benchmarks, hiring advice, and interview prep.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button 
                      onClick={() => { openChat(); handleSend('Python salary in UK'); }}
                      className="px-3 py-1.5 text-xs font-medium bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-full text-foreground transition-colors"
                    >
                      💷 UK Salaries
                    </button>
                    <button 
                      onClick={() => { openChat(); handleSend('How to hire Python developers'); }}
                      className="px-3 py-1.5 text-xs font-medium bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-full text-foreground transition-colors"
                    >
                      👥 Hiring Tips
                    </button>
                    <button 
                      onClick={() => { openChat(); handleSend('Python interview tips'); }}
                      className="px-3 py-1.5 text-xs font-medium bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-full text-foreground transition-colors"
                    >
                      🎯 Interviews
                    </button>
                  </div>
                  <button 
                    onClick={openChat}
                    className="mt-3 w-full px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground rounded-lg transition-colors hover:bg-primary/90"
                  >
                    Start Chat →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chat button */}
        <button
          onClick={openChat}
          className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full cursor-pointer hover:scale-105 hover:shadow-lg hover:shadow-primary/30 transition-all duration-300 shadow-lg active:scale-95 overflow-hidden ring-2 ring-primary/30 hover:ring-primary/60"
          aria-label="Open AI Assistant"
        >
          <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
          <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary absolute top-1 right-1 animate-pulse drop-shadow-lg" />
          
          {/* Notification dot when welcome is showing */}
          {showWelcome && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-card flex items-center justify-center">
              <span className="text-[10px] font-bold text-accent-foreground">1</span>
            </span>
          )}
        </button>
        
        {/* Keyboard shortcut hint (desktop only) */}
        <div className="hidden sm:flex absolute -left-20 bottom-5 items-center gap-1 opacity-60 hover:opacity-100 transition-opacity">
          <kbd className="px-1.5 py-0.5 text-[10px] bg-secondary border border-border rounded font-mono text-muted-foreground">⌘K</kbd>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "fixed z-50 transition-all duration-300",
      isFullscreen 
        ? "inset-0 bg-background" 
        : "bottom-0 right-0 sm:bottom-6 sm:right-6 pointer-events-none"
    )}>
      <div className={cn(
        "flex h-full transition-all duration-300 pointer-events-auto",
        isFullscreen
          ? "items-stretch justify-center p-0"
          : "items-end justify-end p-0 sm:p-0"
      )}>
        <div className={cn(
          "flex flex-col animate-in duration-300 shadow-2xl",
            isFullscreen
              ? "w-full h-full"
              : "w-full sm:w-[480px] h-[85vh] sm:h-[650px] max-h-[700px] sm:max-h-[calc(100vh-3rem)] slide-in-from-bottom-4 sm:slide-in-from-right-4 sm:mb-0 sm:mr-0"
        )}>
          <div className={cn(
            "bg-card border-primary/20 shadow-xl overflow-hidden flex flex-col flex-1",
            isFullscreen ? "border-0" : "border rounded-none sm:rounded-lg"
          )}>
            {/* Voice Mode - temporarily disabled */}
            {/* isVoiceMode ? (
              <VoiceChatMode onClose={() => setIsVoiceMode(false)} />
            ) : */ isCodingMode ? (
              /* Coding Practice Mode */
              <TextCodingMode 
                onClose={() => setIsCodingMode(false)} 
                onSendCode={handleCodeSubmit}
              />
            ) : (
              <>
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border bg-gradient-to-r from-primary/10 to-primary/5 shrink-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden ring-2 ring-primary/30 animate-monty-wave">
                  <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-accent rounded-full border-2 border-card animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-foreground font-orbitron text-sm sm:text-base">Monty</h3>
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary/20 text-primary rounded uppercase tracking-wide">AI</span>
                  </div>
                  <p className="text-xs text-muted-foreground hidden sm:block">Your Python career & hiring assistant</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Voice mode button - temporarily disabled */}
                {/* {showMockInterviewPrompt && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsVoiceMode(true)}
                    className="hover:bg-accent/20 hover:text-accent h-8 w-8 sm:h-10 sm:w-10 animate-pulse"
                    title="Start voice mock interview"
                  >
                    <Mic className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                )} */}
                {/* Clear conversation button */}
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setMessages([]);
                      setLastTopic('default');
                      sessionStorage.removeItem('chatbot_messages');
                      sessionStorage.removeItem('chatbot_last_topic');
                      toast.success('Conversation cleared');
                    }}
                    className="hover:bg-secondary h-8 w-8 sm:h-10 sm:w-10"
                    title="Clear conversation"
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                )}
                {/* Minimize button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setIsOpen(false); setIsMinimized(true); }}
                  className="hover:bg-secondary h-8 w-8 sm:h-10 sm:w-10"
                  title="Minimize chat"
                >
                  <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
                {/* Fullscreen toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="hidden sm:flex hover:bg-secondary h-8 w-8 sm:h-10 sm:w-10"
                  title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </Button>
                {/* Close button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="hover:bg-destructive/10 hover:text-destructive h-8 w-8 sm:h-10 sm:w-10"
                  title="Close chat"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 min-h-0">
              {messages.length === 0 && (
                <div className="text-center py-4 sm:py-8">
                  <div className="relative inline-block mb-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-primary/30">
                      <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
                    </div>
                    <Sparkles className="w-5 h-5 text-primary absolute -top-1 -right-1 animate-pulse" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Hi! I'm Monty 🐍</h4>
                  <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                    Your friendly Python career assistant. Ask me about salaries, hiring, interviews, or career growth!
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-2">
                    AI-powered • Trained on real placement data from Josh Smith
                  </p>
                  <p className="text-xs text-muted-foreground mt-2 mb-4">Try one of these to get started:</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {['Python salary in UK', 'Hiring Python devs', 'Interview tips', 'Career advice'].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => handleSend(suggestion)}
                        className="px-3 py-2 text-xs bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 rounded-full text-foreground font-medium transition-all hover:shadow-sm"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((message, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex gap-3",
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  {message.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full overflow-hidden shrink-0">
                      <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div
                    className={cn(
                      "max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed",
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-md whitespace-pre-wrap'
                        : 'bg-secondary/80 text-foreground rounded-bl-md'
                    )}
                  >
                    {message.role === 'assistant' 
                      ? renderMessageContent(message.content)
                      : message.content
                    }
                  </div>
                  {message.role === 'user' && (
                    <div className="p-1.5 bg-secondary rounded-full h-fit">
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full overflow-hidden shrink-0">
                    <img src={montyAvatar} alt="Monty" className="w-full h-full object-cover" />
                  </div>
                  <div className="bg-secondary px-4 py-2.5 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}

              {/* Coding practice prompt buttons - offer both voice and text */}
              {showCodingPrompt && (
                <div className="pt-2 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2 font-medium">Choose your practice mode:</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setShowCodingPrompt(false);
                        // Navigate to coding interview with voice
                        window.location.href = '/interview-prep?mode=coding-voice';
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <Mic className="w-4 h-4" />
                      Voice Interview
                    </button>
                    <button
                      onClick={() => {
                        setShowCodingPrompt(false);
                        setIsCodingMode(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors"
                    >
                      <Code className="w-4 h-4" />
                      Text Practice
                    </button>
                  </div>
                </div>
              )}

              {/* Mock interview prompt button */}
              {showMockInterviewPrompt && !showCodingPrompt && (
                <div className="pt-2 border-t border-border/50">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setShowMockInterviewPrompt(false);
                        setIsVoiceMode(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <Mic className="w-4 h-4" />
                      Start Voice Interview
                    </button>
                    <a
                      href="/interview-prep"
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                    >
                      View All Interview Types →
                    </a>
                  </div>
                </div>
              )}

              {/* Follow-up suggestions after AI response */}
              {messages.length > 0 && !isLoading && messages[messages.length - 1]?.role === 'assistant' && !showMockInterviewPrompt && !showCodingPrompt && (
                <div className="pt-2 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-2 font-medium">Continue exploring:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentSuggestions.slice(0, 3).map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => handleSend(suggestion)}
                        className="px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 border border-primary/30 hover:border-primary/50 rounded-full text-foreground transition-all hover:shadow-sm"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 sm:p-4 border-t border-border bg-secondary/30 shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask anything about Python careers or hiring..."
                  className="flex-1 px-3 sm:px-4 py-2.5 bg-background border border-border rounded-full text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  disabled={isLoading}
                />
                <Button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isLoading}
                  size="icon"
                  className="rounded-full h-10 w-10 shrink-0"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <div className="hidden sm:flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
                <span>↵ Send</span>
                <span>ESC Close</span>
                <span>🎤 Voice</span>
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
